import {DestroyRef, effect, inject, Injectable, resource, signal} from '@angular/core';
import embedded from '../../../version.json';

/** Период опроса файла версии открытой вкладкой (PAR-12788). */
export const VERSION_POLL_MS = 5 * 60 * 1000;
/** Не чаще: visibilitychange и focus приходят парой при возврате во вкладку. */
export const VERSION_MIN_GAP_MS = 10 * 1000;

/**
 * Проверка версии сборки (PAR-12788): сравнивает версию, вшитую в бандл (`src/version.json`), с файлом `/version.json`
 * на сервере. Проверка идёт при возврате во вкладку (visibilitychange, focus) и раз в 5 минут. Расхождение
 * фиксируется навсегда (`updateAvailable` не возвращается в false): реплика со старой версией, ответившая во время
 * выкатки, не должна прятать баннер. Страницу сервис сам не перезагружает — несохранённая форма пропала бы;
 * `reload()` вызывается только кнопкой баннера.
 *
 * Чтение — нативный fetch, не HttpClient: AuthJwtTokenInterceptor подмешал бы токен в запрос к статическому файлу,
 * а это не вызов Store API (`check:transport`).
 */
@Injectable({providedIn: 'root'})
export class VersionCheckService {
  private readonly tick = signal(0);
  private lastCheck = Date.now();

  private readonly remoteVersion = resource({
    params: () => this.tick(),
    loader: async ({abortSignal}): Promise<string | undefined> => {
      try {
        const response = await fetch('/version.json', {cache: 'no-store', signal: abortSignal});
        if (!response.ok) return undefined;
        const body: unknown = await response.json();
        const version = (body as {version?: unknown} | null)?.version;
        return typeof version === 'string' && version !== '' ? version : undefined;
      } catch {
        // Сеть, не JSON, отмена: состояние не меняется.
        return undefined;
      }
    },
  });

  private readonly updateFlag = signal(false);
  /** Версия на сервере отличается от вшитой; однажды став true, не сбрасывается. */
  readonly updateAvailable = this.updateFlag.asReadonly();

  constructor() {
    // Защёлка в эффекте, а не в linkedSignal: та вычисляется лениво и пропустила бы расхождение, пока никто не читает флаг.
    effect(() => {
      const remote = this.remoteVersion.value();
      if (remote !== undefined && remote !== embedded.version) this.updateFlag.set(true);
    });
    const check = () => this.check();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', check);
    const timer = setInterval(check, VERSION_POLL_MS);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', check);
      clearInterval(timer);
    });
  }

  /** Перезагрузка страницы — только по нажатию кнопки баннера. */
  reload(): void {
    location.reload();
  }

  private check(): void {
    const now = Date.now();
    if (now - this.lastCheck < VERSION_MIN_GAP_MS) return;
    this.lastCheck = now;
    this.tick.update(value => value + 1);
  }
}
